import {
  CascoCommonPeriodEnum,
  CascoDriver,
  CascoTerritoryType,
  DocumentSource,
  OrderDetail,
  PackageEnum,
  PartialFranchiseEnum,
  RefundDestinationEnum
} from '@insurance/api/requests'
import { getEffectiveDateRange } from '@insurance/utils'
import { usePermissionRestriction } from '@shared/hooks'
import { Card, Col, Divider, Form, Modal, Row } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CascoInsuranceProvider } from './CascoInsuranceContext'
import { CascoInsuranceControls } from './CascoInsuranceControls'
import { CorrectionsStep } from './CorrectionsStep'
import { CustomerStep } from './CustomerStep'
import { Details } from './Details'
import { MainStep } from './MainStep'
import { MediaStep } from './MediaStep'
import {
  formProps,
  getCascoValuesFromStorage,
  orderParentOrChildId,
  removeCascoValuesFromStorage,
  saveCascoValuesToStorage
} from './utils'
import {
  iniMixtContractorValues,
  serializeContractorFields
} from '../PersonsFields'

type Props = {
  order?: OrderDetail
  isEditable?: boolean
}

const forms = [
  <MainStep key={0} />,
  <CorrectionsStep key={1} />,
  <MediaStep key={2} />,
  <CustomerStep key={3} />
]

const {
  stepKey,
  priceKey,
  customerProps,
  insurancePackageProps,
  validFromProps,
  vehicleRootProps,
  vehicleIsPremium,
  vehicleIsFromChina,
  territoryProps,
  claimLastYearProps,
  officialDealerRepairProps,
  franchiseProps,
  orderKey,
  cascoRootProps,
  cascoMiniRootProps,
  driversProps,
  costProps,
  effectiveDateProps,
  refundDestinationKey,
  setBeneficiaryKey,
  isOnlineKey,

  ...restObjKeys
} = formProps()

const initVehicleEntity = Object.fromEntries(
  Object.values(restObjKeys)
    .filter(
      ({ name }) => Array.isArray(name) && name[0] === vehicleRootProps.name
    )
    .map(({ name }) => [name[1], undefined])
)

const initCascoEntity = Object.fromEntries(
  Object.values(restObjKeys)
    .filter(
      ({ name }) => Array.isArray(name) && name[0] === cascoRootProps.name
    )
    .map(({ name }) => [name[1], undefined])
)

const initValues = {
  [stepKey.name]: 0,
  [priceKey.name]: undefined,
  [customerProps.name]: undefined,
  [insurancePackageProps.name]: PackageEnum.STANDARD,
  [validFromProps.name]: dayjs(),
  [vehicleRootProps.name]: {
    ...initVehicleEntity,
    [vehicleIsPremium.name[1]]: false,
    [vehicleIsFromChina.name[1]]: false
  },
  [cascoRootProps.name]: {
    ...initCascoEntity,
    [franchiseProps.name[1]]: PartialFranchiseEnum.VARIANT_0_PERCENT,
    [territoryProps.name[1]]: CascoTerritoryType.MD,
    [claimLastYearProps.name[1]]: false,
    [officialDealerRepairProps.name[1]]: false
  },
  [cascoMiniRootProps.name]: undefined,
  [orderKey.name]: undefined,
  [driversProps.name]: undefined,
  [costProps.name]: undefined,
  [effectiveDateProps.name]: undefined,
  [refundDestinationKey.name]: RefundDestinationEnum.CREDIT,
  ...iniMixtContractorValues
}

export const CascoInsurance = ({ order, isEditable }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)
  const { hasPermission } = usePermissionRestriction()

  const isEditableMode = !!(isEditable || order?.parent)

  const handleReadyToSaveData = () => setIsReady(true)

  const saveOnChangeValue = () => {
    const values = form.getFieldsValue(true)

    saveCascoValuesToStorage(values, order?.id)

    return null
  }

  const formattedInitValues = useMemo(() => {
    const detailValues = order?.data

    if (!detailValues) return initValues

    const personDateOfBirth = detailValues?.contractor?.person?.date_of_birth

    if (personDateOfBirth)
      detailValues.contractor!.person!.date_of_birth = dayjs(personDateOfBirth)

    const effectiveDateRange = getEffectiveDateRange(order)
    serializeContractorFields({ hasPermission }, detailValues)

    return {
      ...initValues,
      ...detailValues,
      ...(detailValues.valid_from && {
        valid_from: dayjs(detailValues.valid_from)
      }),
      [effectiveDateProps.name]: effectiveDateRange.minDate,
      ...(detailValues.package === PackageEnum.MINI && {
        casco: { period: CascoCommonPeriodEnum._12 }
      }),
      [stepKey.name]: order.stage || 0,
      [vehicleRootProps.name]: {
        ...detailValues.vehicle,
        year: dayjs(String(detailValues?.vehicle?.year))
      },
      [customerProps.name]: order?.customer,
      [priceKey.name]: { amount: order.full_price, currency: order.currency },
      [orderKey.name]:
        isEditableMode && orderParentOrChildId(order)
          ? order?.parent?.id || order?.id
          : order?.id,
      [driversProps.name]: detailValues.drivers?.map((driver: CascoDriver) => ({
        ...driver,
        ...(driver?.date_of_birth && {
          date_of_birth: dayjs(driver.date_of_birth)
        })
      })),
      [setBeneficiaryKey.name]: !!detailValues.beneficiary,
      [isOnlineKey.name]:
        !isEditableMode && order.source === DocumentSource.WEBSITE
    }
  }, [order, isEditableMode])

  useEffect(() => {
    const values = {
      ...formattedInitValues,
      ...getCascoValuesFromStorage(order?.id)
    }

    form.setFieldsValue(values)
  }, [form, formattedInitValues, order?.id])

  useEffect(() => {
    const values = getCascoValuesFromStorage(order?.id)
    if (!values) return

    const modal = Modal.confirm({
      onCancel: () => {
        removeCascoValuesFromStorage(order?.id)
        setIsReady(false)
        form.setFieldsValue(formattedInitValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => modal.destroy()
  }, [form, t, formattedInitValues, order?.id])

  return (
    <CascoInsuranceProvider
      isEditable={isEditableMode}
      {...(isEditableMode && { order })}
    >
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => <Card>{forms[form.getFieldValue(stepKey.name)]}</Card>}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <CascoInsuranceControls
          steps={forms.length}
          onReadyToSaveData={handleReadyToSaveData}
        />

        {/* use this because when change step and other preserve values doesn't trigger onValuesChange */}
        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </CascoInsuranceProvider>
  )
}
